import {
  Drawer, Box, List, ListItemButton, ListItemIcon, ListItemText,
  Tooltip, Typography, Divider, useTheme, Chip, GlobalStyles,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import { useLocation, useNavigate } from 'react-router-dom';
import { Logo } from '@/components/atoms/Logo';
import { formatBranchLabel } from '@/lib/branches';
import { useAuth } from '@/store/AuthContext';
import { useThemeMode } from '@/store/ThemeContext';
import { appLayout, getGlassStyle } from '@/theme';
import { navItems, navGroups } from './navItems';

export const SIDEBAR_WIDTH = appLayout.sidebarWidth;
export const SIDEBAR_MINI  = appLayout.sidebarMini;

// â”€â”€ Pre-computed bubble data (stable across renders) â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€
const BUBBLES = [
  { id: 0,  w: 28, left: '8%',  dur: '6.5s', delay: '0s',    op: 0.22 },
  { id: 1,  w: 44, left: '22%', dur: '7.8s', delay: '-1.2s', op: 0.15 },
  { id: 2,  w: 18, left: '38%', dur: '5.2s', delay: '-0.5s', op: 0.28 },
  { id: 3,  w: 56, left: '55%', dur: '8.4s', delay: '-2.1s', op: 0.12 },
  { id: 4,  w: 32, left: '70%', dur: '6.1s', delay: '-0.8s', op: 0.20 },
  { id: 5,  w: 22, left: '85%', dur: '5.8s', delay: '-1.7s', op: 0.25 },
  { id: 6,  w: 48, left: '15%', dur: '7.2s', delay: '-3.2s', op: 0.14 },
  { id: 7,  w: 14, left: '42%', dur: '4.9s', delay: '-0.3s', op: 0.30 },
  { id: 8,  w: 38, left: '62%', dur: '6.8s', delay: '-2.6s', op: 0.18 },
  { id: 9,  w: 26, left: '78%', dur: '5.6s', delay: '-1.0s', op: 0.23 },
  { id: 10, w: 50, left: '5%',  dur: '8.1s', delay: '-4.0s', op: 0.11 },
  { id: 11, w: 20, left: '92%', dur: '6.3s', delay: '-0.9s', op: 0.26 },
];

// â”€â”€ CSS keyframe animations â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€
const SidebarAnimationStyles = (
  <GlobalStyles styles={{
    '@keyframes sbBubbleRise': {
      '0%':   { transform: 'translateY(0) scale(1)',       opacity: 0 },
      '10%':  { opacity: 1 },
      '90%':  { opacity: 0.7 },
      '100%': { transform: 'translateY(-115vh) scale(1.25)', opacity: 0 },
    },
  }} />
);

// â”€â”€ Animated overlay components â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€
function BubblesOverlay() {
  return (
    <Box sx={{ position: 'absolute', inset: 0, overflow: 'hidden', pointerEvents: 'none' }}>
      {BUBBLES.map((b) => (
        <Box
          key={b.id}
          sx={{
            position: 'absolute',
            bottom: '-70px',
            left: b.left,
            width: b.w,
            height: b.w,
            borderRadius: '50%',
            background: 'radial-gradient(circle at 30% 30%, rgba(255,255,255,0.45), rgba(120,210,255,0.08))',
            border: '1px solid rgba(255,255,255,0.22)',
            backdropFilter: 'blur(2px)',
            opacity: b.op,
            animation: `sbBubbleRise ${b.dur} ${b.delay} ease-in infinite`,
          }}
        />
      ))}
    </Box>
  );
}

interface SidebarProps {
  open: boolean;
  mobileOpen: boolean;
  onMobileClose: () => void;
}

function NavList({ collapsed }: { collapsed: boolean }) {
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const theme = useTheme();
  const { hasPermission } = useAuth();

  return (
    <List disablePadding sx={{ px: collapsed ? 0.5 : 1.25, flex: 1, overflow: 'auto', py: 1.5 }}>
      {navGroups.map((group) => {
        const items = navItems.filter((n) =>
          n.group === group &&
          (!n.requiredPermission || hasPermission(n.requiredPermission))
        );
        if (items.length === 0) return null;
        return (
          <Box key={group}>
            {!collapsed && (
              <Typography
                variant="caption"
                fontWeight={700}
                color="text.disabled"
                sx={{
                  px: 1.5,
                  pt: 1.5,
                  pb: 0.75,
                  display: 'block',
                  letterSpacing: 1.2,
                  textTransform: 'uppercase',
                  fontSize: '0.675rem',
                }}
              >
                {group}
              </Typography>
            )}
            {collapsed && <Divider sx={{ my: 1, borderColor: alpha(theme.palette.divider, 0.6) }} />}
            {items.map((item) => {
              const exactMatch = pathname === item.path;
              const isParentMatch = pathname.startsWith(item.path + '/');
              // If we have a parent match, only mark it active if the current path isn't an exact match for another items' path
              const active = exactMatch || (isParentMatch && !navItems.some(other => other.path !== item.path && pathname === other.path));
              const Icon = item.icon;
              const btn = (
                <ListItemButton
                  key={item.path}
                  onClick={() => navigate(item.path)}
                  selected={active}
                  sx={{
                    borderRadius: 3,
                    mb: 0.75,
                    minHeight: 52,
                    justifyContent: collapsed ? 'center' : 'flex-start',
                    px: collapsed ? 1.25 : 1.5,
                    py: 0.75,
                    color: active ? theme.palette.common.white : theme.palette.text.secondary,
                    transition: 'all 0.2s ease',
                    '&.Mui-selected, &.Mui-selected:hover': {
                      background: theme.custom.gradients.primary,
                      color: theme.palette.common.white,
                      boxShadow: `0 16px 28px ${alpha(theme.palette.primary.main, 0.28)}`,
                    },
                    '&:hover': {
                      backgroundColor: alpha(theme.palette.primary.main, theme.palette.mode === 'dark' ? 0.16 : 0.08),
                      transform: 'translateX(2px)',
                    },
                  }}
                >
                  <ListItemIcon sx={{ color: 'inherit', minWidth: collapsed ? 0 : 42, mr: collapsed ? 0 : 0.5 }}>
                    <Box
                      sx={{
                        width: 34,
                        height: 34,
                        borderRadius: 2.5,
                        display: 'grid',
                        placeItems: 'center',
                        backgroundColor: active
                          ? alpha(theme.palette.common.white, 0.16)
                          : alpha(theme.palette.primary.main, theme.palette.mode === 'dark' ? 0.14 : 0.08),
                        border: `1px solid ${active ? alpha(theme.palette.common.white, 0.18) : alpha(theme.palette.primary.main, 0.12)}`,
                      }}
                    >
                      <Icon fontSize="small" />
                    </Box>
                  </ListItemIcon>
                  {!collapsed && (
                    <ListItemText
                      primary={item.label}
                      primaryTypographyProps={{
                        fontSize: '0.9rem',
                        fontWeight: active ? 700 : 600,
                        noWrap: true,
                      }}
                    />
                  )}
                </ListItemButton>
              );
              return collapsed
                ? <Tooltip key={item.path} title={item.label} placement="right" arrow>{btn}</Tooltip>
                : btn;
            })}
          </Box>
        );
      })}
    </List>
  );
}

function SidebarContent({ collapsed }: { collapsed: boolean }) {
  const theme = useTheme();
  const { user, selectedBranch } = useAuth();
  const workspaceLabel = formatBranchLabel(selectedBranch || user?.branch || 'Lil Miracles');

  return (
    <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column', p: 1.25, gap: 1 }}>
      <Box
        sx={{
          minHeight: theme.custom.layout.topbarHeight,
          display: 'flex',
          alignItems: 'center',
          justifyContent: collapsed ? 'center' : 'flex-start',
          px: collapsed ? 0 : 2.25,
          flexShrink: 0,
          borderRadius: collapsed ? 4 : 5,
          ...getGlassStyle(theme),
          backgroundColor: alpha(theme.palette.background.paper, theme.palette.mode === 'dark' ? 0.66 : 0.78),
        }}
      >
        <Box>
          <Logo variant={collapsed ? 'icon' : 'full'} height={collapsed ? 32 : 34} />
          {!collapsed && (
            <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block', letterSpacing: '0.08em', textTransform: 'uppercase' }}>
              Preschool command center
            </Typography>
          )}
        </Box>
      </Box>
      <NavList collapsed={collapsed} />
      <Box sx={{ mt: 'auto', pt: 0.5 }}>
        {collapsed ? (
          <Tooltip title={workspaceLabel} placement="right" arrow>
            <Box
              sx={{
                width: 44,
                height: 44,
                mx: 'auto',
                borderRadius: 3,
                display: 'grid',
                placeItems: 'center',
                fontWeight: 700,
                color: 'primary.main',
                backgroundColor: alpha(theme.palette.primary.main, theme.palette.mode === 'dark' ? 0.16 : 0.08),
              }}
            >
              LM
            </Box>
          </Tooltip>
        ) : (
          <Box
            sx={{
              p: 1.5,
              borderRadius: 4,
              ...getGlassStyle(theme),
              backgroundColor: alpha(theme.palette.background.paper, theme.palette.mode === 'dark' ? 0.52 : 0.72),
            }}
          >
            <Typography variant="caption" color="text.secondary" sx={{ letterSpacing: '0.08em', textTransform: 'uppercase' }}>
              Workspace
            </Typography>
            <Typography variant="subtitle2" fontWeight={700} mt={0.75}>
              {workspaceLabel}
            </Typography>
            <Chip
              size="small"
              label={user?.role ?? 'Administrator'}
              sx={{ mt: 1.25, bgcolor: alpha(theme.palette.primary.main, 0.12), color: 'primary.main' }}
            />
          </Box>
        )}
      </Box>
    </Box>
  );
}

export function Sidebar({ open, mobileOpen, onMobileClose }: SidebarProps) {
  const collapsed = !open;
  const theme = useTheme();
  const { sidebarColor } = useThemeMode();

  const { background: glassBackground, ...glassRest } = getGlassStyle(theme);

  const isAnimated = sidebarColor?.startsWith('anim:');

  // Animated presets use a deep dark base colour; their overlay renders on top
  const animBaseBg: Record<string, string> = {
    'anim:bubbles': '#020B18',
  };

  const drawerPaperSx = {
    boxSizing: 'border-box',
    overflowX: 'hidden',
    borderRadius: 0,
    ...glassRest,
    background: isAnimated
      ? (animBaseBg[sidebarColor as string] ?? '#020B18')
      : sidebarColor
        ? (sidebarColor.startsWith('linear-gradient')
            ? sidebarColor
            : alpha(sidebarColor, theme.palette.mode === 'dark' ? 0.9 : 0.84))
        : glassBackground,
    borderRight: `1px solid ${alpha(theme.palette.divider, 0.5)}`,
  };

  const AnimOverlay = isAnimated ? BubblesOverlay : null;

  return (
    <>
      {/* Inject CSS keyframes when any animated preset is active */}
      {isAnimated && SidebarAnimationStyles}

      {/* Mobile drawer */}
      <Drawer
        variant="temporary"
        open={mobileOpen}
        onClose={onMobileClose}
        ModalProps={{ keepMounted: true }}
        sx={{
          display: { xs: 'block', md: 'none' },
          '& .MuiDrawer-paper': {
            width: SIDEBAR_WIDTH,
            borderTopRightRadius: theme.shape.borderRadius * 4.5,
            borderBottomRightRadius: theme.shape.borderRadius * 4.5,
            ...drawerPaperSx,
          },
        }}
      >
        {/* Wrapper keeps overlay behind content without touching Drawer Paper flex layout */}
        <Box sx={{ position: 'relative', height: '100%', display: 'flex', flexDirection: 'column' }}>
          {AnimOverlay && <AnimOverlay />}
          <SidebarContent collapsed={false} />
        </Box>
      </Drawer>

      {/* Desktop drawer */}
      <Drawer
        variant="permanent"
        sx={{
          display: { xs: 'none', md: 'block' },
          '& .MuiDrawer-paper': {
            width: collapsed ? SIDEBAR_MINI : SIDEBAR_WIDTH,
            transition: 'width 0.2s ease',
            ...drawerPaperSx,
          },
        }}
        open
      >
        <Box sx={{ position: 'relative', height: '100%', display: 'flex', flexDirection: 'column' }}>
          {AnimOverlay && <AnimOverlay />}
          <SidebarContent collapsed={collapsed} />
        </Box>
      </Drawer>
    </>
  );
}

